import { Calendar, Cog, Disc, Gauge, Settings, Wrench } from 'lucide-react';
import { PREVENTIVE_SERVICES, SERVICE_STATUS } from './mockWorkshopData';

const SERVICE_ICONS = {
  engine: Wrench,
  cog: Cog,
  settings: Settings,
  disc: Disc,
};

function ProgressGauge({ progress, status, daysRemaining, kmRemaining }) {
  const barColor =
    status === 'critical'
      ? 'bg-red-500'
      : status === 'due_soon'
        ? 'bg-amber-500'
        : 'bg-emerald-500';

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-[10px] text-logi-text0">
        <span className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {daysRemaining}d remaining
        </span>
        <span className="flex items-center gap-1">
          <Gauge className="h-3 w-3" />
          {kmRemaining.toLocaleString()} km
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white ring-1 ring-logi-grid">
        <div
          className={`h-full rounded-full transition-all ${barColor} ${status === 'critical' ? 'animate-pulse' : ''}`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      <p className="text-[9px] text-logi-text0">{progress}% of service interval elapsed</p>
    </div>
  );
}

export default function PreventiveMaintenancePlanner({
  services = PREVENTIVE_SERVICES,
}) {
  const criticalCount = services.filter((s) => s.status === 'critical').length;
  const dueSoonCount = services.filter((s) => s.status === 'due_soon').length;

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-logi-accent" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Preventive Maintenance Planner</h3>
            <p className="text-[11px] text-logi-text0">Scheduled services · Days &amp; KM gauges</p>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          {criticalCount > 0 && (
            <span className="rounded-full bg-red-50 px-2 py-0.5 font-bold text-red-700 ring-1 ring-red-500/25">
              {criticalCount} critical
            </span>
          )}
          {dueSoonCount > 0 && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 font-bold text-amber-700 ring-1 ring-amber-500/25">
              {dueSoonCount} due soon
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
        {services.map((item) => {
          const statusConfig = SERVICE_STATUS[item.status];
          const iconKey =
            item.serviceType === 'Engine Service'
              ? 'engine'
              : item.serviceType === 'Gearbox Overhaul'
                ? 'cog'
                : item.serviceType === 'Suspension Tuning'
                  ? 'settings'
                  : 'disc';
          const Icon = SERVICE_ICONS[iconKey];

          return (
            <div
              key={item.id}
              className="rounded-xl border border-logi-grid bg-white p-4 transition hover:border-logi-grid"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white ring-1 ring-logi-grid">
                    <Icon className="h-4 w-4 text-logi-accent/80" strokeWidth={1.75} />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-bold text-logi-accent">
                      {item.vehicleNumber}
                    </p>
                    <p className="text-[10px] text-logi-text0">{item.vehicleType}</p>
                  </div>
                </div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ring-1 ${statusConfig.className}`}
                >
                  {statusConfig.label}
                </span>
              </div>

              <p className="mt-3 text-sm font-semibold text-logi-text">{item.serviceType}</p>
              <p className="text-[10px] text-logi-text0">
                Last: {item.lastServiceDate} · Due: {item.nextDueDate}
              </p>

              <div className="mt-3">
                <ProgressGauge
                  progress={item.progress}
                  status={item.status}
                  daysRemaining={item.daysRemaining}
                  kmRemaining={item.kmRemaining}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
